import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
    Chip, Button, CircularProgress, Tabs, Tab, Box, Card, CardContent,
    Typography, Alert, ToggleButtonGroup, ToggleButton, Collapse, IconButton,
    Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BuildIcon from '@mui/icons-material/Build';
import CategoryIcon from '@mui/icons-material/Category';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import InfoIcon from '@mui/icons-material/Info';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DataTable from '../../Generic/DataTable';
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
import { useSetMenu } from "../../../hooks/useSetMenu";
import { useSnackbar } from 'notistack';

const MySubmissions = () => {
    const axiosPrivate = useAxiosPrivate();
    const { mainStore } = useStore();
    const { t } = useTranslation();
    const localizedTextsMap = useLocalizedTextsMap();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    useSetMenu(mainStore.MySubmissionsMenuType, mainStore.MySubmissionsMenuNo);

    // Main view mode: 'submissions' or 'requests'
    const [viewMode, setViewMode] = useState('submissions');
    const [submissionType, setSubmissionType] = useState('tools'); // 'tools', 'criteria', or 'features'
    const [requestsContentType, setRequestsContentType] = useState('all'); // 'all', 'criteria', 'criteriaValue'
    const [activeTab, setActiveTab] = useState(0); // 0: All, 1: Pending, 2: Approved, 3: Rejected
    const [pageSize, setPageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [toolSubmissions, setToolSubmissions] = useState([]);
    const [criteriaSubmissions, setCriteriaSubmissions] = useState([]);
    const [criteriaValueSubmissions, setCriteriaValueSubmissions] = useState([]);
    const [toolFeatureSubmissions, setToolFeatureSubmissions] = useState([]);
    const [expandedCriteria, setExpandedCriteria] = useState({});

    // Edit/Delete Requests data
    const [toolPassportRequests, setToolPassportRequests] = useState([]);
    const [criteriaRequests, setCriteriaRequests] = useState([]);
    const [criteriaValueRequests, setCriteriaValueRequests] = useState([]);
    const [toolFeatureRequests, setToolFeatureRequests] = useState([]);
    const [requestsLoading, setRequestsLoading] = useState(false);

    // Detail dialog state for requests
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [detailTab, setDetailTab] = useState(0);

    // Fetch user's tool submissions
    const fetchToolSubmissions = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/ToolPassports/MySubmissions');
            setToolSubmissions(response.data);
        } catch (error) {
            console.error('Error fetching tool submissions:', error);
        }
    }, [axiosPrivate]);

    // Fetch user's criteria submissions
    const fetchCriteriaSubmissions = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/Criterias/MySubmissions');
            setCriteriaSubmissions(response.data);
        } catch (error) {
            console.error('Error fetching criteria submissions:', error);
        }
    }, [axiosPrivate]);

    // Fetch user's criteria value submissions
    const fetchCriteriaValueSubmissions = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/CriteriaValues/MySubmissions');
            setCriteriaValueSubmissions(response.data);
        } catch (error) {
            console.error('Error fetching criteria value submissions:', error);
        }
    }, [axiosPrivate]);

    // Fetch user's tool feature submissions
    const fetchToolFeatureSubmissions = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/ToolFeatures/MySubmissions');
            setToolFeatureSubmissions(response.data);
        } catch (error) {
            console.error('Error fetching tool feature submissions:', error);
        }
    }, [axiosPrivate]);

    // Fetch all submissions
    const fetchSubmissions = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([fetchToolSubmissions(), fetchCriteriaSubmissions(), fetchCriteriaValueSubmissions(), fetchToolFeatureSubmissions()]);
        } catch (error) {
            console.error('Error fetching submissions:', error);
            enqueueSnackbar(t('errorMessageError'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [fetchToolSubmissions, fetchCriteriaSubmissions, fetchCriteriaValueSubmissions, fetchToolFeatureSubmissions, enqueueSnackbar, t]);

    // Fetch user's tool passport edit/delete requests
    const fetchToolPassportRequests = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/ToolPassports/MyTools/AllRequests');
            setToolPassportRequests(response.data.map(r => ({
                ...r,
                contentType: 'ToolPassport',
                itemName: r.originalToolName || r.name,
                uniqueId: `tpr-${r.id}`
            })));
        } catch (error) {
            console.error('Error fetching tool passport requests:', error);
        }
    }, [axiosPrivate]);

    // Fetch user's criteria edit/delete requests
    const fetchCriteriaRequests = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/Criterias/MyCriteria/AllRequests');
            setCriteriaRequests(response.data.map(r => ({
                ...r,
                contentType: 'Criteria',
                itemName: r.originalName || r.name,
                uniqueId: `cr-${r.id}`
            })));
        } catch (error) {
            console.error('Error fetching criteria requests:', error);
        }
    }, [axiosPrivate]);

    // Fetch user's criteria value edit/delete requests
    const fetchCriteriaValueRequests = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/CriteriaValues/MyCriteriaValues/AllRequests');
            setCriteriaValueRequests(response.data.map(r => ({
                ...r,
                contentType: 'CriteriaValue',
                itemName: r.originalValue || r.value,
                uniqueId: `cvr-${r.id}`
            })));
        } catch (error) {
            console.error('Error fetching criteria value requests:', error);
        }
    }, [axiosPrivate]);

    // Fetch user's tool feature edit/delete requests
    const fetchToolFeatureRequests = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/ToolFeatures/MyToolFeatures/AllRequests');
            setToolFeatureRequests(response.data.map(r => ({
                ...r,
                contentType: 'ToolFeature',
                itemName: `${r.toolPassportName} - ${r.criteriaName}`,
                uniqueId: `tfr-${r.id}`
            })));
        } catch (error) {
            console.error('Error fetching tool feature requests:', error);
        }
    }, [axiosPrivate]);

    // Fetch all requests when switching to requests view
    const fetchRequests = useCallback(async () => {
        setRequestsLoading(true);
        try {
            await Promise.all([
                fetchToolPassportRequests(),
                fetchCriteriaRequests(),
                fetchCriteriaValueRequests(),
                fetchToolFeatureRequests()
            ]);
        } catch (error) {
            console.error('Error fetching requests:', error);
        } finally {
            setRequestsLoading(false);
        }
    }, [fetchToolPassportRequests, fetchCriteriaRequests, fetchCriteriaValueRequests, fetchToolFeatureRequests]);

    useEffect(() => {
        fetchSubmissions();
    }, [fetchSubmissions]);

    // Fetch requests when view mode changes to 'requests'
    useEffect(() => {
        if (viewMode === 'requests') {
            fetchRequests();
        }
    }, [viewMode, fetchRequests]);

    // Combine Criteria and CriteriaValues into a single array with type indicator and unique IDs
    const combinedCriteriaSubmissions = useMemo(() => {
        const criteriaWithType = criteriaSubmissions.map(c => ({
            ...c,
            itemType: 'Criteria',
            displayName: c.name,
            uniqueId: `criteria-${c.id}`
        }));
        const criteriaValuesWithType = criteriaValueSubmissions.map(cv => ({
            ...cv,
            itemType: 'CriteriaValue',
            displayName: cv.value,
            uniqueId: `criteriaValue-${cv.id}`
        }));
        // Combine and sort by creation date (newest first)
        return [...criteriaWithType, ...criteriaValuesWithType].sort((a, b) =>
            new Date(b.createdDate || 0) - new Date(a.createdDate || 0)
        );
    }, [criteriaSubmissions, criteriaValueSubmissions]);

    // Group criteria with their values for grouped display
    const groupedCriteriaSubmissions = useMemo(() => {
        return criteriaSubmissions.map(criteria => {
            const relatedValues = criteriaValueSubmissions.filter(
                cv => cv.criteriaId === criteria.id
            );
            return {
                ...criteria,
                criteriaValues: relatedValues
            };
        }).sort((a, b) => new Date(b.createdDate || 0) - new Date(a.createdDate || 0));
    }, [criteriaSubmissions, criteriaValueSubmissions]);

    // Toggle expand for criteria
    const toggleExpandCriteria = useCallback((criteriaId) => {
        setExpandedCriteria(prev => ({
            ...prev,
            [criteriaId]: !prev[criteriaId]
        }));
    }, []);

    // Get current submissions based on type
    const submissions = useMemo(() => {
        if (submissionType === 'tools') return toolSubmissions;
        if (submissionType === 'criteria') return combinedCriteriaSubmissions;
        return toolFeatureSubmissions;
    }, [submissionType, toolSubmissions, combinedCriteriaSubmissions, toolFeatureSubmissions]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    // Filter data based on tab and search
    const filteredData = useMemo(() => {
        let data = submissions;

        // Filter by status based on tab
        switch (activeTab) {
            case 1: // Pending
                data = data.filter(s => s.approvalStatus === 'Pending');
                break;
            case 2: // Approved
                data = data.filter(s => s.approvalStatus === 'Approved');
                break;
            case 3: // Rejected
                data = data.filter(s => s.approvalStatus === 'Rejected');
                break;
            default: // All
                break;
        }

        // Filter by search query
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            data = data.filter(item =>
                item.name?.toLowerCase().includes(query) ||
                item.value?.toLowerCase().includes(query) ||
                item.displayName?.toLowerCase().includes(query) ||
                item.subcategoryName?.toLowerCase().includes(query) ||
                item.categoryName?.toLowerCase().includes(query) ||
                item.criteriaName?.toLowerCase().includes(query)
            );
        }

        return data;
    }, [submissions, activeTab, searchQuery]);

    // Count submissions by status
    // For criteria, count based on grouped view (criteria cards) not combined flat list
    const counts = useMemo(() => {
        if (submissionType === 'criteria') {
            // Count criteria that have the status OR have any values with that status
            return {
                all: groupedCriteriaSubmissions.length,
                pending: groupedCriteriaSubmissions.filter(c =>
                    c.approvalStatus === 'Pending' ||
                    c.criteriaValues.some(cv => cv.approvalStatus === 'Pending')
                ).length,
                approved: groupedCriteriaSubmissions.filter(c =>
                    c.approvalStatus === 'Approved' ||
                    c.criteriaValues.some(cv => cv.approvalStatus === 'Approved')
                ).length,
                rejected: groupedCriteriaSubmissions.filter(c =>
                    c.approvalStatus === 'Rejected' ||
                    c.criteriaValues.some(cv => cv.approvalStatus === 'Rejected')
                ).length,
            };
        }
        return {
            all: submissions.length,
            pending: submissions.filter(s => s.approvalStatus === 'Pending').length,
            approved: submissions.filter(s => s.approvalStatus === 'Approved').length,
            rejected: submissions.filter(s => s.approvalStatus === 'Rejected').length,
        };
    }, [submissionType, submissions, groupedCriteriaSubmissions]);

    // Status chip renderer
    const renderStatusChip = (status) => {
        const statusConfig = {
            'Pending': { color: 'warning', label: t('statusPending') },
            'Approved': { color: 'success', label: t('statusApproved') },
            'Rejected': { color: 'error', label: t('statusRejected') }
        };
        const config = statusConfig[status] || { color: 'default', label: status };
        return <Chip size="small" color={config.color} label={config.label} />;
    };

    // Item type chip renderer for combined criteria view
    const renderItemTypeChip = (type) => {
        if (type === 'Criteria') {
            return <Chip size="small" color="primary" label={t('criteria')} />;
        }
        return <Chip size="small" color="info" variant="outlined" label={t('criteriaValue')} />;
    };

    // Combined all requests (tool passports, criteria, criteria values, tool features)
    const allRequests = useMemo(() => {
        return [...toolPassportRequests, ...criteriaRequests, ...criteriaValueRequests, ...toolFeatureRequests].sort((a, b) =>
            new Date(b.createdDate || 0) - new Date(a.createdDate || 0)
        );
    }, [toolPassportRequests, criteriaRequests, criteriaValueRequests, toolFeatureRequests]);

    // Get filtered requests based on content type and status
    const filteredRequests = useMemo(() => {
        let data = [];
        switch (requestsContentType) {
            case 'all': data = allRequests; break;
            case 'toolPassport': data = toolPassportRequests; break;
            case 'criteria': data = criteriaRequests; break;
            case 'criteriaValue': data = criteriaValueRequests; break;
            case 'toolFeature': data = toolFeatureRequests; break;
            default: data = allRequests;
        }

        // Filter by status based on activeTab
        switch (activeTab) {
            case 1: // Pending
                data = data.filter(r => r.status === 'Pending');
                break;
            case 2: // Approved
                data = data.filter(r => r.status === 'Approved');
                break;
            case 3: // Rejected
                data = data.filter(r => r.status === 'Rejected');
                break;
            default: // All
                break;
        }

        // Filter by search query
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            data = data.filter(item =>
                item.itemName?.toLowerCase().includes(query) ||
                item.originalName?.toLowerCase().includes(query) ||
                item.originalValue?.toLowerCase().includes(query) ||
                item.originalToolName?.toLowerCase().includes(query) ||
                item.toolPassportName?.toLowerCase().includes(query) ||
                item.criteriaName?.toLowerCase().includes(query) ||
                item.name?.toLowerCase().includes(query) ||
                item.value?.toLowerCase().includes(query)
            );
        }

        return data;
    }, [requestsContentType, allRequests, toolPassportRequests, criteriaRequests, criteriaValueRequests, toolFeatureRequests, activeTab, searchQuery]);

    // Requests counts
    const requestsCounts = useMemo(() => {
        let data;
        switch (requestsContentType) {
            case 'all': data = allRequests; break;
            case 'toolPassport': data = toolPassportRequests; break;
            case 'criteria': data = criteriaRequests; break;
            case 'criteriaValue': data = criteriaValueRequests; break;
            case 'toolFeature': data = toolFeatureRequests; break;
            default: data = allRequests;
        }
        return {
            all: data.length,
            pending: data.filter(r => r.status === 'Pending').length,
            approved: data.filter(r => r.status === 'Approved').length,
            rejected: data.filter(r => r.status === 'Rejected').length,
        };
    }, [requestsContentType, allRequests, toolPassportRequests, criteriaRequests, criteriaValueRequests, toolFeatureRequests]);

    // Handle view mode change
    const handleViewModeChange = (event, newMode) => {
        if (newMode !== null) {
            setViewMode(newMode);
            setActiveTab(0);
            setSearchQuery("");
        }
    };

    // Handle view details for requests
    const handleViewDetails = (request) => {
        setSelectedRequest(request);
        setDetailTab(0);
        setDetailDialogOpen(true);
    };

    // Helper to check if a field has changed
    const hasFieldChanged = (originalValue, proposedValue) => {
        if (!proposedValue) return false;
        return proposedValue !== originalValue;
    };

    // Request type chip renderer
    const renderRequestTypeChip = (type) => {
        if (type === 'Edit') {
            return <Chip size="small" color="warning" icon={<EditIcon style={{ fontSize: 14 }} />} label={t('editRequest')} />;
        }
        return <Chip size="small" color="error" icon={<DeleteIcon style={{ fontSize: 14 }} />} label={t('deleteRequest')} />;
    };

    // Request status chip renderer
    const renderRequestStatusChip = (status) => {
        switch (status) {
            case 'Pending':
                return <Chip size="small" color="warning" icon={<PendingActionsIcon />} label={t('statusPending')} />;
            case 'Approved':
                return <Chip size="small" color="success" icon={<CheckCircleIcon />} label={t('statusApproved')} />;
            case 'Rejected':
                return <Chip size="small" color="error" icon={<CancelIcon />} label={t('statusRejected')} />;
            default:
                return <Chip size="small" label={status} />;
        }
    };

    // Content type chip renderer
    const renderContentTypeChip = (type) => {
        switch (type) {
            case 'ToolPassport':
                return <Chip size="small" variant="outlined" color="primary" icon={<BuildIcon style={{ fontSize: 14 }} />} label={t('toolPassport')} />;
            case 'Criteria':
                return <Chip size="small" variant="outlined" color="secondary" icon={<CategoryIcon style={{ fontSize: 14 }} />} label={t('criteria')} />;
            case 'CriteriaValue':
                return <Chip size="small" variant="outlined" color="info" icon={<ListAltIcon style={{ fontSize: 14 }} />} label={t('criteriaValue')} />;
            case 'ToolFeature':
                return <Chip size="small" variant="outlined" color="success" icon={<FeaturedPlayListIcon style={{ fontSize: 14 }} />} label={t('toolFeature')} />;
            default:
                return <Chip size="small" variant="outlined" label={type} />;
        }
    };

    // Filter grouped criteria based on tab and search
    const filteredGroupedCriteria = useMemo(() => {
        let data = groupedCriteriaSubmissions;

        // Filter by status based on tab
        switch (activeTab) {
            case 1: // Pending
                data = data.filter(c => c.approvalStatus === 'Pending' ||
                    c.criteriaValues.some(cv => cv.approvalStatus === 'Pending'));
                break;
            case 2: // Approved
                data = data.filter(c => c.approvalStatus === 'Approved' ||
                    c.criteriaValues.some(cv => cv.approvalStatus === 'Approved'));
                break;
            case 3: // Rejected
                data = data.filter(c => c.approvalStatus === 'Rejected' ||
                    c.criteriaValues.some(cv => cv.approvalStatus === 'Rejected'));
                break;
            default: // All
                break;
        }

        // Filter by search query
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            data = data.filter(item =>
                item.name?.toLowerCase().includes(query) ||
                item.subcategoryName?.toLowerCase().includes(query) ||
                item.categoryName?.toLowerCase().includes(query) ||
                item.criteriaValues.some(cv => cv.value?.toLowerCase().includes(query))
            );
        }

        return data;
    }, [groupedCriteriaSubmissions, activeTab, searchQuery]);

    // Render grouped criteria view
    const renderGroupedCriteriaView = () => {
        if (filteredGroupedCriteria.length === 0) {
            return (
                <div className="text-center p-5">
                    <CategoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                        {t('noSubmissionsYet')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" className="mb-3">
                        {t('noSubmissionsDescription')}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/SubmitCriteria')}
                    >
                        {t("submitNewCriteria")}
                    </Button>
                </div>
            );
        }

        return (
            <div className="criteria-grouped-list">
                {filteredGroupedCriteria.map((criteria) => {
                    const isExpanded = expandedCriteria[criteria.id];
                    const hasValues = criteria.criteriaValues && criteria.criteriaValues.length > 0;
                    const pendingValues = criteria.criteriaValues?.filter(cv => cv.approvalStatus === 'Pending').length || 0;
                    const approvedValues = criteria.criteriaValues?.filter(cv => cv.approvalStatus === 'Approved').length || 0;
                    const rejectedValues = criteria.criteriaValues?.filter(cv => cv.approvalStatus === 'Rejected').length || 0;

                    return (
                        <Card key={criteria.id} className="mb-3" sx={{
                            border: '1px solid',
                            borderColor: criteria.approvalStatus === 'Rejected' ? 'error.light' :
                                         criteria.approvalStatus === 'Pending' ? 'warning.light' : 'divider'
                        }}>
                            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                                {/* Criteria Header */}
                                <div className="d-flex justify-content-between align-items-start">
                                    <div className="d-flex align-items-center gap-2" style={{ flex: 1 }}>
                                        {hasValues && (
                                            <IconButton
                                                size="small"
                                                onClick={() => toggleExpandCriteria(criteria.id)}
                                                sx={{ mr: 1 }}
                                            >
                                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        )}
                                        {!hasValues && <Box sx={{ width: 40 }} />}
                                        <div>
                                            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                                                {criteria.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {criteria.panelName} &gt; {criteria.categoryName} &gt; {criteria.subcategoryName}
                                            </Typography>
                                            {criteria.createdDate && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {t('submittedDate')}: {format(new Date(criteria.createdDate), "dd/MM/yyyy HH:mm")}
                                                </Typography>
                                            )}
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        {/* Weight Coefficient Chip */}
                                        <Chip
                                            size="small"
                                            label={`W = ${criteria.weightCoefficient?.toFixed(2)}`}
                                            color={criteria.weightCoefficient === 1 ? "error" : criteria.weightCoefficient === 0.5 ? "warning" : "success"}
                                        />
                                        {criteria.isMandatory && (
                                            <Chip size="small" label={t("mandatory")} color="primary" variant="outlined" />
                                        )}
                                        {/* Values count */}
                                        {hasValues && (
                                            <Chip
                                                size="small"
                                                label={`${criteria.criteriaValues.length} ${t("values")}`}
                                                variant="outlined"
                                            />
                                        )}
                                        {/* Status */}
                                        {renderStatusChip(criteria.approvalStatus)}
                                    </div>
                                </div>

                                {/* Review Comments for Criteria */}
                                {criteria.reviewComments && (
                                    <Box sx={{ mt: 2, ml: hasValues ? 6 : 5, p: 1.5, bgcolor: criteria.approvalStatus === 'Rejected' ? '#fef2f2' : '#f0f9ff', borderRadius: 1 }}>
                                        <Typography variant="body2" sx={{ color: criteria.approvalStatus === 'Rejected' ? 'error.main' : 'info.main' }}>
                                            <strong>{t('reviewComments')}:</strong> {criteria.reviewComments}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Expanded Values Section */}
                                <Collapse in={isExpanded}>
                                    <Box sx={{ mt: 2, ml: 6 }}>
                                        {hasValues && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                                    {t("criteriaValues")} ({criteria.criteriaValues.length})
                                                    {pendingValues > 0 && <Chip size="small" color="warning" label={`${pendingValues} ${t('statusPending')}`} sx={{ ml: 1 }} />}
                                                    {rejectedValues > 0 && <Chip size="small" color="error" label={`${rejectedValues} ${t('statusRejected')}`} sx={{ ml: 1 }} />}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {criteria.criteriaValues.map((cv) => (
                                                        <Box
                                                            key={cv.id}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                p: 1,
                                                                bgcolor: '#f8fafc',
                                                                borderRadius: 1,
                                                                border: '1px solid',
                                                                borderColor: cv.approvalStatus === 'Rejected' ? 'error.light' : 'divider'
                                                            }}
                                                        >
                                                            <Typography variant="body2">{cv.value}</Typography>
                                                            <div className="d-flex align-items-center gap-2">
                                                                {renderStatusChip(cv.approvalStatus)}
                                                            </div>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                </Collapse>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        );
    };

    // Handle submission type change
    const handleSubmissionTypeChange = (event, newType) => {
        if (newType !== null) {
            setSubmissionType(newType);
            setActiveTab(0); // Reset to "All" tab when switching types
            setSearchQuery(''); // Clear search
        }
    };

    // Columns for tools
    const toolColumns = useMemo(() => [
        {
            field: 'name',
            headerName: t('toolPassportName'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'subcategoryName',
            headerName: t('subcategory'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'categoryName',
            headerName: t('category'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'createdDate',
            headerName: t('submittedDate'),
            flex: 0.7,
            minWidth: 140,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            valueFormatter: (params) => {
                if (params) {
                    return format(new Date(params), "dd/MM/yyyy HH:mm");
                }
                return "-";
            },
        },
        {
            field: 'approvalStatus',
            headerName: t('status'),
            flex: 0.6,
            minWidth: 100,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderStatusChip(params.value),
        },
        {
            field: 'reviewComments',
            headerName: t('reviewComments'),
            flex: 1,
            minWidth: 200,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => {
                if (params.value) {
                    const colorClass = params.row.approvalStatus === 'Rejected' ? 'text-danger' :
                                       params.row.approvalStatus === 'Approved' ? 'text-success' : 'text-muted';
                    return (
                        <span className={colorClass} title={params.value}>
                            {params.value.length > 50 ? `${params.value.substring(0, 50)}...` : params.value}
                        </span>
                    );
                }
                return <span className="text-muted">-</span>;
            },
        },
    ], [t]);

    // Combined columns for criteria and criteria values
    const combinedCriteriaColumns = useMemo(() => [
        {
            field: 'itemType',
            headerName: t('type'),
            flex: 0.5,
            minWidth: 100,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderItemTypeChip(params.value),
        },
        {
            field: 'displayName',
            headerName: t('name'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'criteriaName',
            headerName: t('parentCriteria'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => {
                // For Criteria type, show "-" since it's the parent itself
                if (params.row.itemType === 'Criteria') {
                    return <span className="text-muted">-</span>;
                }
                return params.value || '-';
            },
        },
        {
            field: 'subcategoryName',
            headerName: t('subcategory'),
            flex: 0.7,
            minWidth: 100,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'approvalStatus',
            headerName: t('status'),
            flex: 0.5,
            minWidth: 100,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderStatusChip(params.value),
        },
        {
            field: 'reviewComments',
            headerName: t('reviewComments'),
            flex: 1,
            minWidth: 200,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => {
                if (params.value) {
                    const colorClass = params.row.approvalStatus === 'Rejected' ? 'text-danger' :
                                       params.row.approvalStatus === 'Approved' ? 'text-success' : 'text-muted';
                    return (
                        <span className={colorClass} title={params.value}>
                            {params.value.length > 50 ? `${params.value.substring(0, 50)}...` : params.value}
                        </span>
                    );
                }
                return <span className="text-muted">-</span>;
            },
        },
    ], [t]);

    // Columns for tool features
    const toolFeatureColumns = useMemo(() => [
        {
            field: 'toolPassportName',
            headerName: t('toolPassport'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'criteriaName',
            headerName: t('criteria'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'criteriaValueName',
            headerName: t('criteriaValue'),
            flex: 0.8,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'subcategoryName',
            headerName: t('subcategory'),
            flex: 0.7,
            minWidth: 100,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'approvalStatus',
            headerName: t('status'),
            flex: 0.6,
            minWidth: 100,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderStatusChip(params.value),
        },
        {
            field: 'reviewComments',
            headerName: t('reviewComments'),
            flex: 1,
            minWidth: 200,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => {
                if (params.value) {
                    const colorClass = params.row.approvalStatus === 'Rejected' ? 'text-danger' :
                                       params.row.approvalStatus === 'Approved' ? 'text-success' : 'text-muted';
                    return (
                        <span className={colorClass} title={params.value}>
                            {params.value.length > 50 ? `${params.value.substring(0, 50)}...` : params.value}
                        </span>
                    );
                }
                return <span className="text-muted">-</span>;
            },
        },
    ], [t]);

    // Columns for criteria/value requests
    const requestsColumns = useMemo(() => [
        {
            field: 'contentType',
            headerName: t('contentType'),
            flex: 0.5,
            minWidth: 110,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            headerAlign: 'center',
            align: 'center',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderContentTypeChip(params.value),
        },
        {
            field: 'requestType',
            headerName: t('requestType'),
            flex: 0.5,
            minWidth: 100,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            headerAlign: 'center',
            align: 'center',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderRequestTypeChip(params.value),
        },
        {
            field: 'itemName',
            headerName: t('itemName'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'status',
            headerName: t('status'),
            flex: 0.5,
            minWidth: 100,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => renderRequestStatusChip(params.value),
        },
        {
            field: 'createdDate',
            headerName: t('submittedDate'),
            flex: 0.6,
            minWidth: 120,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            valueFormatter: (params) => {
                if (params) {
                    return format(new Date(params), "dd/MM/yyyy HH:mm");
                }
                return "-";
            },
        },
        {
            field: 'reviewComments',
            headerName: t('reviewComments'),
            flex: 0.8,
            minWidth: 140,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => {
                const comment = params.value;
                if (!comment) return '-';
                return comment.length > 30 ? comment.substring(0, 30) + '...' : comment;
            },
        },
        {
            field: 'actions',
            headerName: t('action'),
            flex: 0.4,
            minWidth: 80,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => (
                <Tooltip title={t('details')}>
                    <IconButton
                        size="small"
                        color="info"
                        onClick={() => handleViewDetails(params.row)}
                    >
                        <InfoIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ], [t]);

    // Get current columns based on submission type and view mode
    const columns = useMemo(() => {
        if (viewMode === 'requests') return requestsColumns;
        if (submissionType === 'tools') return toolColumns;
        if (submissionType === 'criteria') return combinedCriteriaColumns;
        return toolFeatureColumns;
    }, [viewMode, submissionType, toolColumns, combinedCriteriaColumns, toolFeatureColumns, requestsColumns]);

    // Get row ID for DataTable (needed for combined criteria which has uniqueId)
    const getRowId = useMemo(() => {
        if (viewMode === 'requests' || submissionType === 'criteria') {
            return (row) => row.uniqueId;
        }
        return undefined;
    }, [viewMode, submissionType]);

    return (
        <>
            {/* Header - stacks on mobile */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
                <h4 className="mb-0">{t("mySubmissionsTitle")}</h4>
                <div className="d-flex gap-2 flex-wrap w-100 w-md-auto justify-content-start justify-content-md-end">
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/SubmitToolPassport')}
                        size="small"
                        sx={{
                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                            px: { xs: 1, sm: 2 },
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <span className="d-none d-sm-inline">{t("submitNewTool")}</span>
                        <span className="d-sm-none">{t("newTool")}</span>
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/SubmitCriteria')}
                        size="small"
                        sx={{
                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                            px: { xs: 1, sm: 2 },
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <span className="d-none d-sm-inline">{t("submitNewCriteria")}</span>
                        <span className="d-sm-none">{t("newCriteria")}</span>
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/SubmitToolFeature')}
                        size="small"
                        sx={{
                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                            px: { xs: 1, sm: 2 },
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <span className="d-none d-sm-inline">{t("submitNewToolFeature")}</span>
                        <span className="d-sm-none">{t("newFeature")}</span>
                    </Button>
                </div>
            </div>

            {/* Info Alert */}
            <Alert severity="info" className="mb-3">
                {viewMode === 'submissions' ? t("mySubmissionsInfo") : t("myRequestsInfo")}
            </Alert>

            {/* View Mode Toggle - New Submissions vs Edit/Delete Requests */}
            <Box sx={{ mb: 3 }}>
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    aria-label="view mode"
                    size="small"
                    sx={{ mb: 2 }}
                >
                    <ToggleButton value="submissions" sx={{ px: 3 }}>
                        <AddIcon sx={{ mr: 1 }} />
                        {t("newSubmissions")}
                    </ToggleButton>
                    <ToggleButton value="requests" sx={{ px: 3 }}>
                        <HistoryIcon sx={{ mr: 1 }} />
                        {t("editDeleteRequests")} ({allRequests.length})
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Submission Type Toggle (only for submissions view) */}
            {viewMode === 'submissions' && (
                <Box sx={{ mb: 3 }}>
                    <ToggleButtonGroup
                        value={submissionType}
                        exclusive
                        onChange={handleSubmissionTypeChange}
                        aria-label="submission type"
                        size="small"
                    >
                        <ToggleButton value="tools" aria-label="tools">
                            <BuildIcon sx={{ mr: 1 }} />
                            {t("toolPassports")} ({toolSubmissions.length})
                        </ToggleButton>
                        <ToggleButton value="criteria" aria-label="criteria">
                            <CategoryIcon sx={{ mr: 1 }} />
                            {t("criteriaAndValues")} ({criteriaSubmissions.length}{criteriaValueSubmissions.length > 0 ? ` + ${criteriaValueSubmissions.length}` : ''})
                        </ToggleButton>
                        <ToggleButton value="features" aria-label="features">
                            <FeaturedPlayListIcon sx={{ mr: 1 }} />
                            {t("toolFeatures")} ({toolFeatureSubmissions.length})
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            {/* Requests Content Type Filter (only for requests view) */}
            {viewMode === 'requests' && (
                <Box sx={{ mb: 3 }}>
                    <ToggleButtonGroup
                        value={requestsContentType}
                        exclusive
                        onChange={(e, newType) => newType && setRequestsContentType(newType)}
                        aria-label="requests content type"
                        size="small"
                        sx={{ flexWrap: 'wrap' }}
                    >
                        <ToggleButton value="all" aria-label="all">
                            {t("all")} ({allRequests.length})
                        </ToggleButton>
                        <ToggleButton value="toolPassport" aria-label="toolPassport">
                            <BuildIcon sx={{ mr: 1 }} />
                            {t("tools")} ({toolPassportRequests.length})
                        </ToggleButton>
                        <ToggleButton value="criteria" aria-label="criteria">
                            <CategoryIcon sx={{ mr: 1 }} />
                            {t("criteria")} ({criteriaRequests.length})
                        </ToggleButton>
                        <ToggleButton value="criteriaValue" aria-label="criteriaValue">
                            <ListAltIcon sx={{ mr: 1 }} />
                            {t("criteriaValue")} ({criteriaValueRequests.length})
                        </ToggleButton>
                        <ToggleButton value="toolFeature" aria-label="toolFeature">
                            <FeaturedPlayListIcon sx={{ mr: 1 }} />
                            {t("features")} ({toolFeatureRequests.length})
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-md-3 col-sm-6 mb-2">
                    <Card>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="text.primary">
                                {viewMode === 'submissions' ? counts.all : requestsCounts.all}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {viewMode === 'submissions' ? t('totalSubmissions') : t('totalRequests')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-md-3 col-sm-6 mb-2">
                    <Card sx={{ borderLeft: 4, borderColor: 'warning.main' }}>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="warning.main">
                                {viewMode === 'submissions' ? counts.pending : requestsCounts.pending}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('statusPending')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-md-3 col-sm-6 mb-2">
                    <Card sx={{ borderLeft: 4, borderColor: 'success.main' }}>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="success.main">
                                {viewMode === 'submissions' ? counts.approved : requestsCounts.approved}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('statusApproved')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-md-3 col-sm-6 mb-2">
                    <Card sx={{ borderLeft: 4, borderColor: 'error.main' }}>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="error.main">
                                {viewMode === 'submissions' ? counts.rejected : requestsCounts.rejected}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('statusRejected')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab label={`${t('all')} (${viewMode === 'submissions' ? counts.all : requestsCounts.all})`} />
                    <Tab label={`${t('statusPending')} (${viewMode === 'submissions' ? counts.pending : requestsCounts.pending})`} />
                    <Tab label={`${t('statusApproved')} (${viewMode === 'submissions' ? counts.approved : requestsCounts.approved})`} />
                    <Tab label={`${t('statusRejected')} (${viewMode === 'submissions' ? counts.rejected : requestsCounts.rejected})`} />
                </Tabs>
            </Box>

            {/* Search */}
            <div className="mb-3">
                <input
                    type="text"
                    className="form-control"
                    placeholder={t("searchPlaceholder")}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ maxWidth: '300px' }}
                />
            </div>

            {/* Data Table / Grouped View */}
            {viewMode === 'submissions' ? (
                // Submissions View
                loading ? (
                    <div className="d-flex justify-content-center p-5">
                        <CircularProgress />
                    </div>
                ) : submissionType === 'criteria' ? (
                    // Use grouped criteria view for criteria submissions
                    renderGroupedCriteriaView()
                ) : filteredData.length === 0 ? (
                    <div className="text-center p-5">
                        <BuildIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="textSecondary">
                            {t('noSubmissionsYet')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" className="mb-3">
                            {t('noSubmissionsDescription')}
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/SubmitToolPassport')}
                        >
                            {t("submitNewTool")}
                        </Button>
                    </div>
                ) : (
                    <DataTable
                        rows={filteredData}
                        columns={columns}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        localeTextsMap={localizedTextsMap}
                        getRowId={getRowId}
                    />
                )
            ) : (
                // Requests View
                requestsLoading ? (
                    <div className="d-flex justify-content-center p-5">
                        <CircularProgress />
                    </div>
                ) : filteredRequests.length === 0 ? (
                    <div className="text-center p-5">
                        <HistoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="textSecondary">
                            {t('noRequestsYet')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {t('noRequestsDescription')}
                        </Typography>
                    </div>
                ) : (
                    <DataTable
                        rows={filteredRequests}
                        columns={requestsColumns}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        localeTextsMap={localizedTextsMap}
                        getRowId={(row) => row.uniqueId}
                    />
                )
            )}

            {/* Request Detail Dialog */}
            <Dialog
                open={detailDialogOpen}
                onClose={() => setDetailDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon color="primary" />
                    {t('requestDetails')}
                </DialogTitle>
                <DialogContent>
                    {selectedRequest && (
                        <Box>
                            {/* Status Alert */}
                            <Alert
                                severity={
                                    selectedRequest.status === 'Approved' ? 'success' :
                                    selectedRequest.status === 'Rejected' ? 'error' : 'warning'
                                }
                                sx={{ mb: 2 }}
                            >
                                <strong>{t('status')}: </strong>
                                {selectedRequest.status === 'Pending' && t('statusPending')}
                                {selectedRequest.status === 'Approved' && t('statusApproved')}
                                {selectedRequest.status === 'Rejected' && t('statusRejected')}
                            </Alert>

                            {/* Tabs for Edit requests */}
                            {selectedRequest.requestType === 'Edit' && (
                                <Tabs
                                    value={detailTab}
                                    onChange={(e, newValue) => setDetailTab(newValue)}
                                    sx={{ mb: 2 }}
                                >
                                    <Tab icon={<InfoIcon />} iconPosition="start" label={t('requestDetails')} />
                                    <Tab icon={<CompareArrowsIcon />} iconPosition="start" label={t('viewChanges')} />
                                </Tabs>
                            )}

                            {/* Tab 0: Request Info */}
                            {(detailTab === 0 || selectedRequest.requestType === 'Delete') && (
                                <>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <tbody>
                                            <tr>
                                                <td style={labelCellStyle}>{t('contentType')}</td>
                                                <td style={valueCellStyle}>{renderContentTypeChip(selectedRequest.contentType)}</td>
                                            </tr>
                                            <tr>
                                                <td style={labelCellStyle}>{t('requestType')}</td>
                                                <td style={valueCellStyle}>{renderRequestTypeChip(selectedRequest.requestType)}</td>
                                            </tr>
                                            {selectedRequest.contentType === 'ToolPassport' && (
                                                <tr>
                                                    <td style={labelCellStyle}>{t('originalToolName')}</td>
                                                    <td style={valueCellStyle}>{selectedRequest.originalToolName}</td>
                                                </tr>
                                            )}
                                            {selectedRequest.contentType === 'Criteria' && (
                                                <tr>
                                                    <td style={labelCellStyle}>{t('originalCriteria')}</td>
                                                    <td style={valueCellStyle}>{selectedRequest.originalName}</td>
                                                </tr>
                                            )}
                                            {selectedRequest.contentType === 'CriteriaValue' && (
                                                <tr>
                                                    <td style={labelCellStyle}>{t('originalCriteriaValue')}</td>
                                                    <td style={valueCellStyle}>{selectedRequest.originalValue}</td>
                                                </tr>
                                            )}
                                            {selectedRequest.contentType === 'ToolFeature' && (
                                                <>
                                                    <tr>
                                                        <td style={labelCellStyle}>{t('toolPassport')}</td>
                                                        <td style={valueCellStyle}>{selectedRequest.toolPassportName}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={labelCellStyle}>{t('criteria')}</td>
                                                        <td style={valueCellStyle}>{selectedRequest.criteriaName}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={labelCellStyle}>{t('criteriaValue')}</td>
                                                        <td style={valueCellStyle}>{selectedRequest.criteriaValueName}</td>
                                                    </tr>
                                                </>
                                            )}
                                            {selectedRequest.reason && (
                                                <tr>
                                                    <td style={labelCellStyle}>{t('reason')}</td>
                                                    <td style={valueCellStyle}>{selectedRequest.reason}</td>
                                                </tr>
                                            )}
                                            <tr>
                                                <td style={labelCellStyle}>{t('submittedDate')}</td>
                                                <td style={valueCellStyle}>
                                                    {selectedRequest.createdDate
                                                        ? format(new Date(selectedRequest.createdDate), "dd/MM/yyyy HH:mm")
                                                        : '-'}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    {/* Review Info (if reviewed) */}
                                    {(selectedRequest.status === 'Approved' || selectedRequest.status === 'Rejected') && (
                                        <>
                                            <Divider sx={{ my: 2 }} />
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                {t('reviewInfo')}
                                            </Typography>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <tbody>
                                                    {selectedRequest.reviewDate && (
                                                        <tr>
                                                            <td style={labelCellStyle}>{t('reviewDate')}</td>
                                                            <td style={valueCellStyle}>
                                                                {format(new Date(selectedRequest.reviewDate), "dd/MM/yyyy HH:mm")}
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {selectedRequest.reviewComments && (
                                                        <tr>
                                                            <td style={labelCellStyle}>{t('reviewComments')}</td>
                                                            <td style={{
                                                                ...valueCellStyle,
                                                                color: selectedRequest.status === 'Rejected' ? '#d32f2f' : 'inherit',
                                                                fontWeight: selectedRequest.status === 'Rejected' ? 500 : 'normal'
                                                            }}>
                                                                {selectedRequest.reviewComments}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </>
                                    )}
                                </>
                            )}

                            {/* Tab 1: View Changes (only for Edit requests) */}
                            {detailTab === 1 && selectedRequest.requestType === 'Edit' && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                                        {t('proposedChanges')}
                                    </Typography>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ background: '#f5f5f5' }}>
                                                <th style={{ padding: '10px', border: '1px solid #e0e0e0', textAlign: 'left' }}>{t('field')}</th>
                                                <th style={{ padding: '10px', border: '1px solid #e0e0e0', textAlign: 'left' }}>{t('originalValues')}</th>
                                                <th style={{ padding: '10px', border: '1px solid #e0e0e0', textAlign: 'left' }}>{t('proposedChanges')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedRequest.contentType === 'ToolPassport' && (
                                                <>
                                                    <tr>
                                                        <td style={labelCellStyle}>{t('name')}</td>
                                                        <td style={valueCellStyle}>{selectedRequest.originalToolName || '-'}</td>
                                                        <td style={{
                                                            ...valueCellStyle,
                                                            background: hasFieldChanged(selectedRequest.originalToolName, selectedRequest.name) ? '#fff3e0' : 'inherit'
                                                        }}>
                                                            {selectedRequest.name || '-'}
                                                            {hasFieldChanged(selectedRequest.originalToolName, selectedRequest.name) && (
                                                                <Chip size="small" label={t('changed')} color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                                                            )}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td style={labelCellStyle}>{t('toolURL')}</td>
                                                        <td style={valueCellStyle}>{selectedRequest.originalToolURL || '-'}</td>
                                                        <td style={{
                                                            ...valueCellStyle,
                                                            background: hasFieldChanged(selectedRequest.originalToolURL, selectedRequest.toolURL) ? '#fff3e0' : 'inherit'
                                                        }}>
                                                            {selectedRequest.toolURL || '-'}
                                                            {hasFieldChanged(selectedRequest.originalToolURL, selectedRequest.toolURL) && (
                                                                <Chip size="small" label={t('changed')} color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                                                            )}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td style={labelCellStyle}>{t('function')}</td>
                                                        <td style={valueCellStyle}>{selectedRequest.originalFunction || '-'}</td>
                                                        <td style={{
                                                            ...valueCellStyle,
                                                            background: hasFieldChanged(selectedRequest.originalFunction, selectedRequest.function) ? '#fff3e0' : 'inherit'
                                                        }}>
                                                            {selectedRequest.function || '-'}
                                                            {hasFieldChanged(selectedRequest.originalFunction, selectedRequest.function) && (
                                                                <Chip size="small" label={t('changed')} color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                                                            )}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td style={labelCellStyle}>{t('abstractionLevel')}</td>
                                                        <td style={valueCellStyle}>{selectedRequest.originalAbstractionLevel || '-'}</td>
                                                        <td style={{
                                                            ...valueCellStyle,
                                                            background: hasFieldChanged(selectedRequest.originalAbstractionLevel, selectedRequest.abstractionLevel) ? '#fff3e0' : 'inherit'
                                                        }}>
                                                            {selectedRequest.abstractionLevel || '-'}
                                                            {hasFieldChanged(selectedRequest.originalAbstractionLevel, selectedRequest.abstractionLevel) && (
                                                                <Chip size="small" label={t('changed')} color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                                                            )}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td style={labelCellStyle}>{t('ioConnection')}</td>
                                                        <td style={valueCellStyle}>{selectedRequest.originalIOConnection || '-'}</td>
                                                        <td style={{
                                                            ...valueCellStyle,
                                                            background: hasFieldChanged(selectedRequest.originalIOConnection, selectedRequest.ioConnection) ? '#fff3e0' : 'inherit'
                                                        }}>
                                                            {selectedRequest.ioConnection || '-'}
                                                            {hasFieldChanged(selectedRequest.originalIOConnection, selectedRequest.ioConnection) && (
                                                                <Chip size="small" label={t('changed')} color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                                                            )}
                                                        </td>
                                                    </tr>
                                                </>
                                            )}
                                            {selectedRequest.contentType === 'Criteria' && (
                                                <>
                                                    <tr>
                                                        <td style={labelCellStyle}>{t('criteriaName')}</td>
                                                        <td style={valueCellStyle}>{selectedRequest.originalName || '-'}</td>
                                                        <td style={{
                                                            ...valueCellStyle,
                                                            background: hasFieldChanged(selectedRequest.originalName, selectedRequest.name) ? '#fff3e0' : 'inherit'
                                                        }}>
                                                            {selectedRequest.name || '-'}
                                                            {hasFieldChanged(selectedRequest.originalName, selectedRequest.name) && (
                                                                <Chip size="small" label={t('changed')} color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                                                            )}
                                                        </td>
                                                    </tr>
                                                </>
                                            )}
                                            {selectedRequest.contentType === 'CriteriaValue' && (
                                                <tr>
                                                    <td style={labelCellStyle}>{t('criteriaValueName')}</td>
                                                    <td style={valueCellStyle}>{selectedRequest.originalValue || '-'}</td>
                                                    <td style={{
                                                        ...valueCellStyle,
                                                        background: hasFieldChanged(selectedRequest.originalValue, selectedRequest.value) ? '#fff3e0' : 'inherit'
                                                    }}>
                                                        {selectedRequest.value || '-'}
                                                        {hasFieldChanged(selectedRequest.originalValue, selectedRequest.value) && (
                                                            <Chip size="small" label={t('changed')} color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                            {selectedRequest.contentType === 'ToolFeature' && (
                                                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                                    {t('noEditableFieldsForToolFeature')}
                                                </Typography>
                                            )}
                                        </tbody>
                                    </table>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialogOpen(false)} variant="outlined">
                        {t('Close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Table cell styles for detail dialog
const labelCellStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #e0e0e0',
    width: '35%',
    fontWeight: 600,
    color: '#374151',
    background: '#f8fafc'
};

const valueCellStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #e0e0e0',
    color: '#1e293b'
};

export default observer(MySubmissions);
